import { useTwilioPhone } from '@/hooks/useTwilioPhone';
import { getPrimaryPhone } from '@/services/contact-point';
import { markNotificationAsArchived, moveNotificationToInbox } from '@bariendo/lib/notifications';
import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { createReference, resolveId } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconArchive, IconArchiveOff, IconPhoneOutgoing } from '@tabler/icons-react';
import { usePostHog } from 'posthog-js/react';
import { trackCallInitiated } from '@/services/analytics';
import { NotificationItem, RowActionKind } from './types';

type ActionsProps = {
  notification: NotificationItem;
  action?: RowActionKind;
  onRefresh?: () => void;
};

export const RowActions = ({ notification, action = RowActionKind.ARCHIVE, onRefresh }: ActionsProps) => {
  const medplum = useMedplum();
  const { makeCall } = useTwilioPhone();
  const posthog = usePostHog();

  // Get patient from the task.for.resource (already included via GraphQL fragment)
  const forResource = notification.task.for?.resource;
  const patient =
    forResource && 'patientName' in forResource && 'telecom' in forResource
      ? (forResource as unknown as Patient)
      : null;

  const archiveAction = action === RowActionKind.ARCHIVE ? markNotificationAsArchived : moveNotificationToInbox;
  const archiveIcon = action === RowActionKind.ARCHIVE ? <IconArchive size={14} /> : <IconArchiveOff size={14} />;

  return (
    <Group>
      <Tooltip label={action === RowActionKind.ARCHIVE ? 'Archive' : 'Move to inbox'}>
        <ActionIcon
          variant="subtle"
          color="dark"
          size="xs"
          onClick={async () => {
            if (notification.task) {
              const taskRef = createReference(notification.task);
              await archiveAction(medplum, taskRef);
              onRefresh?.();
            }
          }}
        >
          {archiveIcon}
        </ActionIcon>
      </Tooltip>
      {patient && (
        <Tooltip label="Call patient">
          <ActionIcon
            variant="subtle"
            color="dark"
            size="xs"
            onClick={() => {
              // Track call initiated from inbox row
              const patientId = resolveId(notification.task.for);
              if (patientId) {
                trackCallInitiated(posthog, {
                  source: 'inbox row',
                  patient_id: patientId,
                  inbox_item_task_id: notification.task.id,
                });
              }
              makeCall(getPrimaryPhone(patient.telecom)!.value!);
            }}
          >
            <IconPhoneOutgoing size={14} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};
